import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { data, portal, type Connection } from "@aawaz/api-client";
import {
  Badge, Button, Card, ChannelLogo, CopyButton, EmptyState, ErrorBanner, Field, Icon, Input, Modal, Notice, Select, SkeletonRows, fmtDate, humanize,
} from "@aawaz/ui";
import { useClients } from "../components/ItemPanel";
import { useSession } from "../session";

interface Connector { key: string; name: string; auth: string; binding_types: string[] }
interface Agent {
  connection_id: string; status: string; write_consent: Record<string, boolean>; agent_version: string | null; last_seen_at?: string | null;
}
type Listed = Connection & { last_sync_at?: string | null };
interface BooksSource { binding_id: string; connection_id: string; company_name: string | null; client_org_id: string | null;
  last_synced_at: string | null; last_error: string | null; counts: Record<string, number> }
interface FormField { name: string; type: string; required?: boolean; values?: string[]; help?: string }
interface Start { connection: Connection; auth: { kind: string; auth_url?: string; code?: string; expires_at?: string; form?: { fields: FormField[] } } }
interface Bindable { id: string; name: string; type: string; has_children: boolean; suggested_client_org_id: string | null }
interface Bound { id: string; binding_type: string; external_ref: string; client_org_id: string | null; config: Record<string, unknown> | null }

/** Names people use, instead of the provider's product names. */
export const CHANNEL_NAME: Record<string, string> = {
  whatsapp: "WhatsApp", email_inbound: "Email inbox", gdrive: "Google Drive", onedrive: "OneDrive", zoho_books: "Zoho Books",
  tally: "Tally", gsp: "GST portal", filesystem: "Shared folder",
};
const nameOf = (key: string) => CHANNEL_NAME[key] ?? humanize(key);

/** Accounting software and drives the firm can let AawazAI write into (approved entries, filed copies). */
const WRITABLE = ["gdrive", "onedrive", "tally", "zoho_books"];
const FOLDERS = ["gdrive", "onedrive", "filesystem"];

/** The integration gallery. `ca` entries only show for the CA edition. */
const CATALOGUE: { key: string; group: string; blurb: string; ca?: boolean }[] = [
  { key: "whatsapp", group: "Collect", blurb: "Clients and staff send bills to the AawazAI number, directly or in a client group. Each one is read, named and filed, and the sender gets a receipt." },
  { key: "email_inbound", group: "Collect", blurb: "A forwarding address per client. Attachments are read and filed the same way." },
  { key: "gdrive", group: "File", blurb: "Files every document into the client's folder by year, month and type. Your own files are never touched." },
  { key: "onedrive", group: "File", blurb: "The same filing, into OneDrive or SharePoint." },
  { key: "zoho_books", group: "Books", blurb: "Two-way sync of parties, ledgers and vouchers every 15 minutes. Approved bills post back as drafts." },
  { key: "tally", group: "Books", blurb: "Through a small agent on the PC that runs Tally. Posting stays off until that PC allows it." },
  { key: "gsp", group: "GST", blurb: "GSTR-2B for each client, matched against the bills you hold to find ITC at risk.", ca: true },
];

/** Overview: what is connected (health, sync, write-back, linking to clients) and the gallery to add more. */
export function ConnectionsPage() {
  const s = useSession();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["connections"], queryFn: () =>
    data<{ data: Listed[]; tally_agents: Agent[] }>(portal.GET("/portal/v1/connections")) });
  const connectors = useQuery({ queryKey: ["connectors"], queryFn: () => data<{ data: Connector[] }>(portal.GET("/portal/v1/connectors")) });
  const hasBooks = (list.data?.data ?? []).some((c) => c.connector_key === "zoho_books");
  const books = useQuery({ enabled: hasBooks, queryKey: ["ca-books-sources"], refetchInterval: 30_000,
    queryFn: () => data<{ data: BooksSource[] }>(portal.GET("/portal/v1/ca/books/sources")) });
  const syncNow = useMutation({
    mutationFn: async (connectionId: string) => {
      for (const b of (books.data?.data ?? []).filter((x) => x.connection_id === connectionId)) {
        await data(portal.POST("/portal/v1/ca/books/sources/{binding_id}:sync", { params: { path: { binding_id: b.binding_id } } }));
      }
    },
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ["ca-books-sources"] }), 5000),
  });
  const [start, setStart] = useState<Start | null>(null);
  const [binding, setBinding] = useState<Connection | null>(null);
  const [enabling, setEnabling] = useState<Listed | null>(null);
  const [removing, setRemoving] = useState<Listed | null>(null);
  // back from Google / Microsoft / Zoho consent (/connections/<id>?connected=1): go straight to linking it to a client
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const id = location.pathname.split("/")[2];
    if (!id || !new URLSearchParams(location.search).has("connected")) return;
    const conn = list.data?.data.find((c) => c.id === id);
    if (!conn) return;
    if (s.can("manage_connections")) setBinding(conn);
    navigate("/channels", { replace: true });
  }, [location.pathname, location.search, list.data, navigate, s]);
  const oauthError = new URLSearchParams(location.search).get("oauth_error");
  const test = useMutation({
    mutationFn: (id: string) => data<{ ok: boolean }>(portal.POST("/portal/v1/connections/{connection_id}/test", { params: { path: { connection_id: id } } })),
    onSettled: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
  const write = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => data(portal.POST("/portal/v1/connections/{connection_id}:write",
      { params: { path: { connection_id: id } }, body: { enabled } })),
    onSuccess: () => setEnabling(null),
    onSettled: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => portal.DELETE("/portal/v1/connections/{connection_id}", { params: { path: { connection_id: id } } }),
    onSuccess: () => setRemoving(null),
    onSettled: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
  const begin = useMutation({
    mutationFn: (key: string) => data<Start>(portal.POST("/portal/v1/connections", { body: { connector_key: key, config: {} } })),
    onSuccess: (r) => {
      if (r.auth.kind === "auth_url" && r.auth.auth_url) window.location.assign(r.auth.auth_url); // Google / Microsoft / Zoho consent
      else setStart(r);
      void qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });
  const pair = useMutation({
    mutationFn: async (r: Listed): Promise<Start> => ({ connection: r, auth: { kind: "pairing_code", ...(await data<{ code: string; expires_at: string }>(
      portal.POST("/portal/v1/tally/{connection_id}/pairing-code", { params: { path: { connection_id: r.id } } }))) } }),
    onSuccess: setStart,
  });
  const agents = list.data?.tally_agents ?? [];
  const live = (list.data?.data ?? []).filter((c) => c.status !== "revoked");
  const available = new Set((connectors.data?.data ?? []).map((c) => c.key));
  const catalogue = CATALOGUE.filter((c) => available.has(c.key) && (!c.ca || s.has("ca")));
  const health = (c: Listed) => c.last_error || c.status === "error" ? "red" : c.status === "active" ? "green" : "amber";
  const providerName = (key: string) => connectors.data?.data.find((x) => x.key === key)?.name;
  const title = (c: Listed) => !c.display_name || c.display_name.toLowerCase() === c.connector_key || c.display_name === providerName(c.connector_key)
    ? nameOf(c.connector_key) : c.display_name;
  return (
    <>
      <ErrorBanner error={list.error ?? test.error ?? write.error ?? begin.error ?? pair.error} />
      {oauthError ? <Notice>{oauthError === "access_denied" ? "The account was not connected: access was not granted."
        : oauthError === "VALIDATION_FAILED" ? "The sign-in link expired. Start again from the gallery below."
        : `The account could not be connected (${oauthError}). Try again; if it keeps failing, check the app's OAuth settings.`}</Notice> : null}

      <Card title="Connected" subtitle={live.length ? `${live.length} account${live.length === 1 ? "" : "s"} · health, sync and what AawazAI may write` : undefined} flush>
        {list.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={3} /></div> : live.length === 0 ? (
          <EmptyState icon="plug" title="Nothing connected yet">Start with WhatsApp to receive bills, then a drive to file them into.</EmptyState>
        ) : (
          <div className="ui-conn-list">
            {live.map((r) => (
              <div key={r.id} className="ui-conn">
                <span className="ui-tile-logo"><ChannelLogo kind={r.connector_key} size={24} /></span>
                <div className="ui-conn-main">
                  <div className="ui-row" style={{ gap: 8 }}>
                    <strong>{title(r)}</strong><span className={`ui-dot ui-${health(r)}`} />
                    {title(r) !== nameOf(r.connector_key) ? <span className="ui-tag">{nameOf(r.connector_key)}</span> : null}
                  </div>
                  <div className="ui-conn-meta">
                    {r.last_error ? <span className="ui-text-red">{r.last_error.slice(0, 140)}</span>
                      : r.connector_key === "zoho_books" ? <BooksSync sources={(books.data?.data ?? []).filter((b) => b.connection_id === r.id)} />
                      : r.connector_key === "tally" ? <TallyPc agents={agents.filter((a) => a.connection_id === r.id)} />
                      : r.status === "pending" ? (r.connector_key === "whatsapp"
                        ? <span className="ui-text-amber">Not connected: this set-up was never finished. <Link to="/channels/whatsapp">Connect WhatsApp</Link></span>
                        : <span className="ui-text-amber">Waiting to finish setup</span>)
                      : r.connector_key === "whatsapp" ? <span>Receiving · <Link to="/channels/whatsapp">groups and numbers</Link></span>
                      : r.connector_key === "gsp" ? <span>GSTIN <span className="ui-mono">{String(r.config.gstin ?? "—")}</span> · {humanize(String(r.config.family ?? "gst"))}</span>
                      : r.last_sync_at ? <span>Synced {fmtDate(r.last_sync_at)}</span> : <span>Active</span>}
                  </div>
                </div>
                {WRITABLE.includes(r.connector_key) ? (
                  <label className="ui-switch-inline" title={s.can("enable_write") ? "Let AawazAI write approved entries or filed copies into this account"
                    : "Only an owner of the firm can turn this on or off"}>
                    <input type="checkbox" role="switch" className="ui-switch" checked={!!r.config.write_enabled} disabled={!s.can("enable_write")}
                      onChange={(e) => e.target.checked ? setEnabling(r) : write.mutate({ id: r.id, enabled: false })} />
                    <span>{FOLDERS.includes(r.connector_key) ? "Filing" : "Posting"} {r.config.write_enabled ? "on" : "off"}</span>
                  </label>
                ) : null}
                {s.can("manage_connections") ? (
                  <div className="ui-conn-actions">
                    {!["whatsapp", "gsp"].includes(r.connector_key) ? <Button size="sm" icon="link" onClick={() => setBinding(r)}>Link clients</Button> : null}
                    {r.connector_key === "zoho_books" ? <Button size="sm" icon="refresh" onClick={() => syncNow.mutate(r.id)}
                      busy={syncNow.isPending && syncNow.variables === r.id}>Sync</Button> : null}
                    {r.connector_key === "tally" ? <Button size="sm" icon="key" onClick={() => pair.mutate(r)}
                      busy={pair.isPending && pair.variables?.id === r.id}>Pair PC</Button> : null}
                    {r.status !== "pending" ? <Button size="sm" variant="ghost" onClick={() => test.mutate(r.id)} busy={test.isPending && test.variables === r.id}
                            title="Check the account still answers">Test</Button> : null}
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(r)}
                            title="Stop using this account. Documents already received are kept.">{r.status === "pending" ? "Remove" : "Disconnect"}</Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="ui-section-head"><h2>Add a channel</h2><span>Connect as many as you need; each account is linked to clients after sign-in.</span></div>
      <div className="ui-tiles">
        {catalogue.map((c) => {
          const n = live.filter((x) => x.connector_key === c.key).length;
          return (
            <div key={c.key} className="ui-card ui-tile">
              <div className="ui-tile-head">
                <span className="ui-tile-logo"><ChannelLogo kind={c.key} size={26} /></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="ui-tile-eyebrow">{c.group}</span>
                  <strong className="ui-tile-name">{nameOf(c.key)}</strong>
                </div>
                {n ? <Badge value={n > 1 ? `${n} connected` : "connected"} tone="green" /> : null}
              </div>
              <p>{c.blurb}</p>
              {c.key === "whatsapp" && live.some((x) => x.connector_key === "whatsapp" && x.shared) ? (
                <div className="ui-tile-foot">
                  <Button size="sm" variant="secondary" icon="users" style={{ width: "100%" }}
                          onClick={() => navigate("/channels/whatsapp")}>Groups and numbers</Button>
                </div>
              ) : s.can("manage_connections") ? (
                <div className="ui-tile-foot">
                  <Button size="sm" variant={n ? "secondary" : "primary"} icon={n ? "plus" : "link"} style={{ width: "100%" }}
                          onClick={() => begin.mutate(c.key)} busy={begin.isPending && begin.variables === c.key}>
                    {n ? "Add another account" : `Connect ${nameOf(c.key)}`}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <Modal open={!!start} size={start?.auth.kind === "pairing_code" ? "md" : "sm"} onClose={() => setStart(null)}
             icon={start ? <ChannelLogo kind={start.connection.connector_key} size={24} /> : null}
             title={start?.auth.kind === "pairing_code" ? "Pair the Tally PC" : start ? `Connect ${nameOf(start.connection.connector_key)}` : ""}
             subtitle={start?.auth.kind === "pairing_code" ? "Tally stays on that PC. A small agent there syncs it with AawazAI."
               : start?.auth.kind === "form" ? "These details stay encrypted and are only used to talk to this account." : undefined}>
        {start ? <CompleteAuth start={start} onCancel={() => setStart(null)} onDone={() => {
          // Guided setup (UX-24): once connected, the next step is choosing its client and folder.
          const next = start.connection;
          setStart(null);
          void qc.invalidateQueries({ queryKey: ["connections"] });
          if (!["whatsapp", "gsp"].includes(next.connector_key) && start.auth.kind !== "pairing_code" && s.can("manage_connections")) setBinding(next);
        }} /> : null}
      </Modal>

      <Modal open={!!binding} size="lg" onClose={() => setBinding(null)}
             icon={binding ? <ChannelLogo kind={binding.connector_key} size={24} /> : null}
             title={binding ? `Link ${nameOf(binding.connector_key)} to clients` : ""}
             subtitle={binding ? LINK_HINT[binding.connector_key] ?? LINK_HINT.folder : undefined}>
        {binding ? <Bindings connection={binding} onClose={() => setBinding(null)} /> : null}
      </Modal>

      <Modal open={!!removing} size="sm" onClose={() => setRemoving(null)}
             icon={removing ? <ChannelLogo kind={removing.connector_key} size={24} /> : null}
             title={removing ? (removing.status === "pending" ? `Remove ${title(removing)}?` : `Disconnect ${title(removing)}?`) : ""}>
        {removing ? (
          <>
            <ErrorBanner error={disconnect.error} />
            <p style={{ margin: 0 }}>
              {removing.status === "pending" ? "This setup was never finished. Removing it changes nothing else."
                : "AawazAI stops reading from and writing to this account, and its links to clients are switched off."}
            </p>
            {removing.status !== "pending" ? (
              <ul className="ui-checklist">
                <li><Icon name="check" size={16} />Documents already received stay in your inbox and vault.</li>
                <li><Icon name="check" size={16} />Nothing in the account itself is deleted.</li>
                <li><Icon name="check" size={16} />You can connect it again later and link your clients to it again.</li>
              </ul>
            ) : null}
            <div className="ui-dialog-actions">
              <Button variant="ghost" onClick={() => setRemoving(null)}>Keep</Button>
              <Button variant="danger" busy={disconnect.isPending} onClick={() => disconnect.mutate(removing.id)}>
                {removing.status === "pending" ? "Remove" : "Disconnect"}</Button>
            </div>
          </>
        ) : null}
      </Modal>

      <Modal open={!!enabling} size="sm" onClose={() => setEnabling(null)}
             icon={enabling ? <ChannelLogo kind={enabling.connector_key} size={24} /> : null}
             title={enabling ? (FOLDERS.includes(enabling.connector_key) ? "Turn on filing" : "Turn on posting") : ""}>
        {enabling ? (
          <>
            <p style={{ margin: 0 }}>
              {FOLDERS.includes(enabling.connector_key)
                ? `AawazAI will create its own folder in ${nameOf(enabling.connector_key)} and keep filed copies there.`
                : `AawazAI will be able to create draft entries in ${nameOf(enabling.connector_key)} for bills you approve.`}
            </p>
            <ul className="ui-checklist">
              <li><Icon name="check" size={16} />Only files and entries AawazAI created are ever changed.</li>
              <li><Icon name="check" size={16} />Nothing of yours is moved, overwritten or deleted.</li>
              <li><Icon name="check" size={16} />You can turn this off at any time; it is recorded in the audit log.</li>
              {s.me?.owner_totp !== false
                ? <li><Icon name="shield" size={16} />While this is on, the firm's owners also confirm sign-in with an authenticator app.</li> : null}
            </ul>
            <div className="ui-dialog-actions">
              <Button variant="ghost" onClick={() => setEnabling(null)}>Cancel</Button>
              <Button variant="primary" busy={write.isPending} onClick={() => write.mutate({ id: enabling.id, enabled: true })}>Turn on</Button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
}

const LINK_HINT: Record<string, string> = {
  folder: "Pick the folder that holds each client's papers. Everything new in it is read and filed under that client.",
  zoho_books: "Link each Zoho organization to the client whose books it holds. Sync runs every 15 minutes.",
  tally: "Link each Tally company on the paired PC to its client.",
  email_inbound: "Give each client a forwarding address. Anything sent to it is filed under that client.",
  gsp: "Link the GST login of each client to fetch their GSTR-2B.",
};

function CompleteAuth({ start, onDone, onCancel }: { start: Start; onDone: () => void; onCancel: () => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const complete = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/connections/{connection_id}:complete",
      { params: { path: { connection_id: start.connection.id } }, body: { payload: values } })),
    onSuccess: onDone,
  });
  if (start.auth.kind === "pairing_code") {
    const code = start.auth.code ?? "";
    const steps = `Install the AawazAI Tally Agent on the PC that runs Tally, then run: tally-agent pair --code ${code}`;
    return (
      <>
        <div className="ui-code-box">
          <span className="ui-code-label">Pairing code</span>
          <span className="ui-code">{code.replace(/(\d{4})(?=\d)/g, "$1 ")}</span>
          <span className="ui-hint">Valid until {fmtDate(start.auth.expires_at)}</span>
        </div>
        <ol className="ui-numbered">
          <li><strong>Install the AawazAI Tally Agent</strong><span>On the PC where Tally runs.</span></li>
          <li><strong>Pair it with this code</strong><span>Run <code className="ui-mono">tally-agent pair --code {code}</code> before it expires.</span></li>
          <li><strong>Keep Tally open</strong><span>Records sync while that PC and the agent are on. Posting stays off until someone at the PC allows it per company.</span></li>
        </ol>
        <div className="ui-dialog-actions">
          <span className="ui-spacer"><CopyButton text={steps} label="Copy steps to send" /></span>
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </>
    );
  }
  if (start.auth.kind === "form" && start.auth.form) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); complete.mutate(); }}>
        <ErrorBanner error={complete.error} />
        {start.auth.form.fields.map((f) => (
          <Field key={f.name} label={humanize(f.name)} hint={f.help}>{(id) => f.values ? (
            <Select id={id} required={f.required} value={values[f.name] ?? ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                    options={[{ value: "", label: "Choose" }, ...f.values.map((v) => ({ value: v, label: v }))]} />
          ) : (
            <Input id={id} required={f.required} type={f.type === "secret" ? "password" : "text"} autoComplete="off"
                   value={values[f.name] ?? ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
          )}</Field>
        ))}
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" type="submit" busy={complete.isPending}>Connect</Button>
        </div>
      </form>
    );
  }
  return (
    <>
      <EmptyState icon="checkCircle" title="Connected">{start.connection.connector_key === "whatsapp"
        ? "WhatsApp is ready on the AawazAI number. Next, create a group per client or map the numbers that send to it directly."
        : "The account is ready. Link it to clients next."}</EmptyState>
      <div className="ui-dialog-actions"><Button variant="primary" onClick={onDone}>Continue</Button></div>
    </>
  );
}

function Bindings({ connection, onClose }: { connection: Connection; onClose: () => void }) {
  const qc = useQueryClient();
  const clients = useClients();
  const [path, setPath] = useState<{ id: string; name: string }[]>([]);
  const [filter, setFilter] = useState("");
  const [alias, setAlias] = useState({ ref: "", client: "" });
  const key = connection.connector_key;
  const current = useQuery({ queryKey: ["bindings", connection.id], queryFn: () =>
    data<{ data: Bound[] }>(portal.GET("/portal/v1/bindings", { params: { query: { connection_id: connection.id } } })) });
  const isFolder = FOLDERS.includes(key);
  const isBooks = key === "zoho_books";
  const isPicker = isFolder || isBooks || key === "tally";
  const parent = path.at(-1)?.id;
  const pick = useQuery({
    enabled: isPicker, queryKey: ["bindables", connection.id, parent ?? ""],
    queryFn: () => data<{ data: Bindable[] }>(portal.GET("/portal/v1/connections/{connection_id}/bindables",
      { params: { path: { connection_id: connection.id }, query: parent ? { parent } : {} } })),
  });
  const bind = useMutation({
    mutationFn: (b: { binding_type: string; external_ref: string; client_org_id: string | null; config?: Record<string, unknown> }) =>
      data(portal.POST("/portal/v1/bindings", { body: { connection_id: connection.id, config: {}, ...b } })),
    onSuccess: () => { setAlias({ ref: "", client: "" }); void qc.invalidateQueries({ queryKey: ["bindings", connection.id] }); },
  });
  const unbind = useMutation({
    mutationFn: (id: string) => portal.DELETE("/portal/v1/bindings/{binding_id}", { params: { path: { binding_id: id } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bindings", connection.id] }),
  });
  const names = new Map((clients.data?.data ?? []).map((c) => [c.id, c.display_name]));
  const clientOpts = [{ value: "", label: "Choose client" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))];
  const linked = current.data?.data ?? [];
  const linkedRef = new Map(linked.map((b) => [b.external_ref, b]));
  const label = (b: Bound) => String(b.config?.name ?? b.config?.company_name ?? b.config?.path ?? b.external_ref);
  const unit = isFolder ? "folder" : isBooks ? "organization" : key === "tally" ? "company" : key === "email_inbound" ? "address" : "source";
  const plural = (n: number) => n === 1 ? unit : unit === "company" ? "companies" : unit === "address" ? "addresses" : `${unit}s`;
  const rows = useMemo(() => (pick.data?.data ?? []).filter((r) => !filter || r.name.toLowerCase().includes(filter.toLowerCase())), [pick.data, filter]);
  return (
    <>
      <ErrorBanner error={bind.error ?? unbind.error ?? pick.error} />
      <section className="ui-dialog-section">
        <div className="ui-dialog-section-head"><h3>Linked</h3><span className="ui-hint">{linked.length} {plural(linked.length)}</span></div>
        {current.isLoading ? <SkeletonRows rows={2} /> : linked.length === 0 ? (
          <p className="ui-hint" style={{ margin: 0 }}>Nothing linked yet. {isPicker || key === "email_inbound" ? `Choose a client for each ${unit} below.` : ""}</p>
        ) : (
          <div className="ui-pick-list">
            {linked.map((b) => (
              <div key={b.id} className="ui-pick-row">
                <span className="ui-pick-icon"><Icon name={isFolder ? "folder" : key === "email_inbound" ? "mail" : "building"} size={16} /></span>
                <span className="ui-pick-name" title={b.external_ref}>{label(b)}</span>
                {key === "email_inbound" ? <CopyButton text={b.external_ref} label="Copy" /> : null}
                <span className="ui-tag ui-tag-brand">{b.client_org_id ? names.get(b.client_org_id) ?? "…" : "AI routing"}</span>
                <Button size="sm" variant="ghost" onClick={() => unbind.mutate(b.id)} busy={unbind.isPending && unbind.variables === b.id}>Unlink</Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {isPicker ? (
        <section className="ui-dialog-section">
          <div className="ui-dialog-section-head">
            <h3>{isFolder ? "Folders" : isBooks ? "Zoho organizations" : "Tally companies"}</h3>
            {(pick.data?.data.length ?? 0) > 6 ? (
              <div className="ui-search ui-search-sm"><Icon name="search" size={14} />
                <input aria-label="Filter" placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} /></div>
            ) : null}
          </div>
          {isFolder ? (
            <nav className="ui-crumbs" aria-label="Folder path">
              <button onClick={() => setPath([])} aria-current={!path.length ? "page" : undefined}><Icon name="home" size={14} />My drive</button>
              {path.map((p, i) => (
                <span key={p.id}><Icon name="chevron" size={12} />
                  <button onClick={() => setPath(path.slice(0, i + 1))} aria-current={i === path.length - 1 ? "page" : undefined}>{p.name}</button></span>
              ))}
            </nav>
          ) : null}
          {pick.isLoading ? <SkeletonRows rows={4} /> : rows.length === 0 ? (
            <p className="ui-hint" style={{ margin: 0 }}>{filter ? "No match." : isFolder ? "This folder has no sub-folders."
              : key === "tally" ? "No companies yet. They appear once the Tally PC is paired and Tally is open." : "No organizations found for this sign-in."}</p>
          ) : (
            <div className="ui-pick-list ui-pick-scroll">
              {rows.map((r) => {
                const done = linkedRef.get(r.id);
                return (
                  <div key={r.id} className="ui-pick-row">
                    <span className="ui-pick-icon"><Icon name={isFolder ? "folder" : "building"} size={16} /></span>
                    {r.has_children && isFolder ? (
                      <button className="ui-pick-name ui-pick-open" onClick={() => { setFilter(""); setPath([...path, { id: r.id, name: r.name }]); }}>
                        {r.name}<Icon name="chevron" size={14} /></button>
                    ) : <span className="ui-pick-name">{r.name}</span>}
                    {done ? <span className="ui-tag ui-tag-brand"><Icon name="check" size={12} />{done.client_org_id ? names.get(done.client_org_id) : "Linked"}</span> : (
                      <BindRow suggestion={r.suggested_client_org_id} options={clientOpts} busy={bind.isPending && bind.variables?.external_ref === r.id}
                        onBind={(client) => bind.mutate({ binding_type: isFolder ? "folder" : isBooks ? "zoho_org" : "tally_company", external_ref: r.id,
                          client_org_id: client, config: isFolder || isBooks ? { name: r.name } : { company_name: r.name } })} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {isFolder ? <p className="ui-hint" style={{ margin: 0 }}>Suggestions come from folder names that look like a client. Confirm each one.</p> : null}
        </section>
      ) : null}

      {key === "email_inbound" ? (
        <section className="ui-dialog-section">
          <div className="ui-dialog-section-head"><h3>Make an address for a client</h3></div>
          <form className="ui-inline-form" onSubmit={(e) => { e.preventDefault(); bind.mutate({ binding_type: "email_alias", external_ref: "", client_org_id: alias.client }); }}>
            <Select aria-label="Client" value={alias.client} onChange={(e) => setAlias({ ...alias, client: e.target.value })} options={clientOpts} />
            <Button type="submit" variant="primary" busy={bind.isPending} disabled={!alias.client}>Make address</Button>
          </form>
          <p className="ui-hint" style={{ margin: 0 }}>The address appears above. Give it to the client, or forward their emails to it: every attachment is read and filed under them.</p>
        </section>
      ) : null}
      <div className="ui-dialog-actions"><Button variant="primary" onClick={onClose}>Done</Button></div>
    </>
  );
}

function BindRow({ suggestion, options, onBind, busy }: {
  suggestion: string | null; options: { value: string; label: string }[]; onBind: (c: string) => void; busy?: boolean;
}) {
  const [v, setV] = useState(suggestion ?? "");
  return (
    <div className="ui-pick-bind">
      {suggestion && v === suggestion ? <span className="ui-tag ui-tag-amber"><Icon name="sparkles" size={12} />Suggested</span> : null}
      <Select aria-label="Client" value={v} onChange={(e) => setV(e.target.value)} options={options} />
      <Button size="sm" variant={v ? "primary" : "secondary"} disabled={!v} busy={busy} onClick={() => onBind(v)}>{suggestion && v === suggestion ? "Confirm" : "Link"}</Button>
    </div>
  );
}

/** Whether the Tally PC is reachable now; offline is never shown as live. */
function TallyPc({ agents }: { agents: Agent[] }) {
  if (!agents.length) return <span className="ui-text-amber">Not paired yet. Use Pair PC.</span>;
  return <>{agents.map((a) => {
    const allowed = Object.values(a.write_consent).filter(Boolean).length;
    return (
      <span key={a.connection_id + a.agent_version}>
        {a.status === "online" ? "PC online" : `PC offline${a.last_seen_at ? ` since ${fmtDate(a.last_seen_at)}` : ""}; records are not current until it is back`}
        {" · "}posting allowed for {allowed} compan{allowed === 1 ? "y" : "ies"}
      </span>
    );
  })}</>;
}

/** Zoho Books: when the linked organizations last synced, or why syncing stopped. */
function BooksSync({ sources }: { sources: BooksSource[] }) {
  if (!sources.length) return <span className="ui-text-amber">No organization linked yet. Use Link clients.</span>;
  const bad = sources.find((b) => b.last_error);
  const last = sources.map((b) => b.last_synced_at).filter(Boolean).sort().at(-1);
  if (bad?.last_error) return <span className="ui-text-red">{bad.last_error.startsWith("REAUTH_REQUIRED") ? "Sign-in expired. Reconnect Zoho." : bad.last_error.slice(0, 120)}</span>;
  return <span>{sources.length} organization{sources.length === 1 ? "" : "s"} · {last ? `synced ${fmtDate(last)}` : "waiting for first sync"}</span>;
}
